/**
 * Тонкие обёртки fetch для repo-эндпоинтов.
 *
 * Эндпоинты сами живут в `packages/server/src/main.ts` — здесь только
 * нормализованные сигнатуры + типы ответов. Каждая функция бросает
 * `Error` с осмысленным message при non-2xx.
 */

import type { ProgressResponse, Severity } from '@pflow/shared';
export type { ProgressResponse };

export interface RepoRef {
    owner: string;
    repo:  string;
    /** Ветка/тег/SHA или undefined → default-ветка. */
    ref?:  string;
    /** Поддиректория для monorepo. */
    path?: string;
}

// ─── /projects ────────────────────────────────────────────────────────

export interface ProjectsResponse {
    sha:      string;
    branch?:  string;
    /** Список поддиректорий с маркером `.pflow/`. Пустая строка — корень. */
    projects: string[];
}

export async function fetchProjects(input: { owner: string; repo: string; ref?: string }): Promise<ProjectsResponse> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/projects`, location.origin);
    if (input.ref) url.searchParams.set('ref', input.ref);
    return fetchJson<ProjectsResponse>(url);
}

// ─── /refs ────────────────────────────────────────────────────────────

/**
 * Реальный контракт server-эндпоинта `/api/repo/:owner/:repo/refs`:
 * branches приходят как массив строк (имена), prs — flat-объекты без
 * head/base-разделения. См. packages/server/src/main.ts → `/refs`.
 *
 * Tags эндпоинт пока не возвращает — добавим, если понадобится.
 */
export interface RefsResponse {
    defaultBranch: string;
    branches:      string[];
    prs: Array<{
        number: number;
        title:  string;
        sha:    string;
        /** Для same-repo PR — имя ветки; для fork — `refs/pull/N/head`. */
        ref:    string;
        isFork: boolean;
    }>;
}

export async function fetchRefs(input: { owner: string; repo: string }): Promise<RefsResponse> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/refs`, location.origin);
    return fetchJson<RefsResponse>(url);
}

// ─── /entity-source ───────────────────────────────────────────────────

/**
 * Контракт server-эндпоинта `/api/repo/:owner/:repo/entity-source`.
 * Сервер возвращает EntitySource из packages/server/src/entity.ts +
 * sha/branch из git.resolveSha. Поле с текстом блока — `blockText`,
 * не `text`.
 */
export interface EntitySourceResponse {
    blockText: string;
    file:      string;
    language:  'pflow' | 'svcflow' | 'dflow';
    range: {
        start: { line: number; character: number };
        end:   { line: number; character: number };
    };
    /** Markdown-срез сущности (context-builder) — контент вкладки «Обзор». */
    summaryMd: string | null;
    /**
     * Находки health-чекеров по этой сущности (фильтр уже посчитанного
     * `Snapshot.health`, не новый анализ). Пусто, если чекеры молчат либо
     * снапшот ещё не собран.
     */
    health?: EntityHealthFinding[];
    sha:    string;
    branch?: string;
}

/** Находка чекера + к какому чекеру относится (для заголовка в UI). */
export interface EntityHealthFinding {
    message:     string;
    severity:    Severity;
    aspectId:    string;
    aspectTitle: string;
    fix?:        { hint: string; field?: string; example?: string };
}

export async function fetchEntitySource(
    input: RepoRef & { type: string; name: string },
): Promise<EntitySourceResponse> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/entity-source`, location.origin);
    if (input.ref)  url.searchParams.set('ref',  input.ref);
    if (input.path) url.searchParams.set('path', input.path);
    url.searchParams.set('type', input.type);
    url.searchParams.set('name', input.name);
    return fetchJson<EntitySourceResponse>(url);
}

/** LLM-срез сущности для «Обзора». text: null — LLM выключен → fallback. */
export async function fetchEntitySummary(
    input: RepoRef & { type: string; name: string },
): Promise<{ text: string | null; model?: string }> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/entity-summary`, location.origin);
    if (input.ref)  url.searchParams.set('ref',  input.ref);
    if (input.path) url.searchParams.set('path', input.path);
    url.searchParams.set('type', input.type);
    url.searchParams.set('name', input.name);
    return fetchJson<{ text: string | null; model?: string }>(url);
}

// ─── /entity-history ──────────────────────────────────────────────────

/** Одна запись истории сущности — коммит + срез блока на тот момент. */
export interface EntityHistoryEntry {
    sha:     string;
    /** Первая строка commit message. */
    message: string;
    /** Author date в ISO 8601. */
    date:    string;
    author:  string;
    /** Текст блока сущности в этом коммите; null если файл/блок не извлёкся. */
    source:  string | null;
}

export interface EntityHistoryResponse {
    history: EntityHistoryEntry[];
    /** Путь файла относительно корня репо. */
    file:    string;
}

/**
 * Контракт server-эндпоинта `/api/repo/:owner/:repo/entity-history`.
 * В отличие от entity-source принимает точный `sha` (SHA-checkout должен
 * быть в кэше — он есть после загрузки snapshot'а) и возвращает таймлайн
 * версий блока сущности (только коммиты, где блок реально менялся).
 */
export async function fetchEntityHistory(
    input: { owner: string; repo: string; sha: string; path?: string; type: string; name: string; limit?: number },
): Promise<EntityHistoryResponse> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/entity-history`, location.origin);
    url.searchParams.set('sha', input.sha);
    if (input.path)  url.searchParams.set('path', input.path);
    url.searchParams.set('type', input.type);
    url.searchParams.set('name', input.name);
    if (input.limit) url.searchParams.set('limit', String(input.limit));
    return fetchJson<EntityHistoryResponse>(url);
}

// ─── /entity-backlinks ────────────────────────────────────────────────

export interface BacklinkEntry {
    sourceType:  string;
    sourceTitle: string;
    sourceName:  string;
    relation:    string;
    refItemType: string;
    file:        string;
}

export interface ForwardLinkEntry {
    targetType:  string;
    targetTitle: string;
    targetName:  string;
    relation:    string;
    refItemType: string;
    file:        string;
}

export interface EntityBacklinksResponse {
    backlinks:    BacklinkEntry[];
    forwardLinks: ForwardLinkEntry[];
    /** Markdown-документация типа сущности из hover-docs. */
    docs:         string | null;
    /** Рыночная сущность (research-модель конкурента, Принадлежность: рынок). */
    market:       boolean;
}

export async function fetchEntityBacklinks(
    input: RepoRef & { type: string; name: string },
): Promise<EntityBacklinksResponse> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/entity-backlinks`, location.origin);
    if (input.ref)  url.searchParams.set('ref',  input.ref);
    if (input.path) url.searchParams.set('path', input.path);
    url.searchParams.set('type', input.type);
    url.searchParams.set('name', input.name);
    return fetchJson<EntityBacklinksResponse>(url);
}

// ─── /progress ────────────────────────────────────────────────────────

export async function fetchProgress(input: { owner: string; repo: string; ref?: string }): Promise<ProgressResponse> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/progress`, location.origin);
    if (input.ref) url.searchParams.set('ref', input.ref);
    return fetchJson<ProgressResponse>(url);
}

// ─── /sync-status ─────────────────────────────────────────────────────

export interface SyncStatusResponse {
    /** 'syncing' если POST /sync уже выполняется для этого ref. */
    status: 'idle' | 'syncing';
    /** HEAD SHA постоянного checkout'а или null если checkout ещё не создан. */
    sha:    string | null;
}

export async function fetchSyncStatus(input: { owner: string; repo: string; ref?: string }): Promise<SyncStatusResponse> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/sync-status`, location.origin);
    if (input.ref) url.searchParams.set('ref', input.ref);
    return fetchJson<SyncStatusResponse>(url);
}

// ─── /sync ────────────────────────────────────────────────────────────

export interface SyncResponse {
    sha:      string;
    prevSha:  string | null;
    /** true если HEAD изменился после fetch. */
    changed:  boolean;
    snapshot: import('@pflow/shared/v2').Snapshot;
}

export async function triggerSync(input: RepoRef): Promise<SyncResponse> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/sync`, location.origin);
    if (input.ref)  url.searchParams.set('ref',  input.ref);
    if (input.path) url.searchParams.set('path', input.path);
    const res = await fetch(url.pathname + url.search, { method: 'POST', credentials: 'include' });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`${url.pathname} ${res.status}: ${text || res.statusText}`);
    }
    return await res.json() as SyncResponse;
}

// ─── /commits ─────────────────────────────────────────────────────────

/**
 * Один коммит из истории проекта. Зеркало `CommitInfo` из
 * packages/server/src/git.ts. Параллельный тип, не shared — endpoint
 * нужен только этому фронту, не растягиваем shared-контракт.
 */
export interface CommitInfo {
    sha:     string;
    /** Author date в ISO 8601. */
    date:    string;
    author:  string;
    /** Первая строка commit message. */
    message: string;
}

export interface CommitsResponse {
    commits: CommitInfo[];
}

export async function fetchCommits(input: RepoRef & { limit?: number; all?: boolean }): Promise<CommitsResponse> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/commits`, location.origin);
    if (input.ref)              url.searchParams.set('ref',  input.ref);
    if (input.path !== undefined) url.searchParams.set('path', input.path);
    if (input.limit)            url.searchParams.set('limit', String(input.limit));
    // all=1 — вернуть все коммиты проекта, без фильтра по .pflow-файлам.
    // Нужно при сравнении разных веток/PR: история PR-ветки может почти не
    // трогать .pflow напрямую, и отфильтрованный таймлайн оказывается пустым.
    if (input.all)              url.searchParams.set('all', '1');
    return fetchJson<CommitsResponse>(url);
}

/**
 * merge-base двух веток — коммит, от которого head ответвилась от base.
 * sha=null, если общего предка нет (несвязанные истории) или вычислить
 * не удалось. base/head — имена ref'ов (ветка/тег/`refs/pull/N/head`).
 */
export async function fetchMergeBase(
    input: { owner: string; repo: string; base: string; head: string },
): Promise<{ sha: string | null }> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/merge-base`, location.origin);
    url.searchParams.set('base', input.base);
    url.searchParams.set('head', input.head);
    return fetchJson<{ sha: string | null }>(url);
}

// ─── /diff-v2 ─────────────────────────────────────────────────────────

import type { RepoDiff } from '@pflow/shared/v2/diff';
export type { RepoDiff };

export async function fetchRepoDiff(input: {
    owner:    string;
    repo:     string;
    baseSha:  string;
    headSha:  string;
    path?:    string;
    baseRef?: string;
    headRef?: string;
}): Promise<RepoDiff> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/diff-v2`, location.origin);
    url.searchParams.set('base', input.baseSha);
    url.searchParams.set('head', input.headSha);
    if (input.path !== undefined) url.searchParams.set('path', input.path);
    if (input.baseRef)            url.searchParams.set('baseRef', input.baseRef);
    if (input.headRef)            url.searchParams.set('headRef', input.headRef);
    return fetchJson<RepoDiff>(url);
}

// ─── /chat/sessions ───────────────────────────────────────────────────

export interface ChatSessionItem {
    id:        string;
    title:     string;
    updatedAt: number;
}

export interface ChatMessageItem {
    id:        string;
    sessionId: string;
    role:      'user' | 'assistant';
    content:   string;
    /** JSON UIMessage.parts (v7) — восстанавливает tool-вызовы; null у старых. */
    parts:     string | null;
    createdAt: number;
}

export async function fetchChatSessions(
    input: { owner: string; repo: string },
): Promise<{ sessions: ChatSessionItem[] }> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/chat/sessions`, location.origin);
    return fetchJson<{ sessions: ChatSessionItem[] }>(url);
}

export async function fetchChatMessages(
    input: { owner: string; repo: string; sessionId: string },
): Promise<{ messages: ChatMessageItem[] }> {
    const url = new URL(
        `/api/repo/${input.owner}/${input.repo}/chat/sessions/${input.sessionId}/messages`,
        location.origin,
    );
    return fetchJson<{ messages: ChatMessageItem[] }>(url);
}

export async function deleteChatSession(
    input: { owner: string; repo: string; sessionId: string },
): Promise<void> {
    const url = new URL(
        `/api/repo/${input.owner}/${input.repo}/chat/sessions/${input.sessionId}`,
        location.origin,
    );
    const res = await fetch(url.pathname, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) throw new Error(`${url.pathname} ${res.status}`);
}

export interface PflowComment {
    id:           number;
    url:          string;
    target:       string;
    /** Поддиректория проекта в монорепо ('' — корень). */
    path:         string;
    text:         string;
    author:       string;
    authorAvatar: string;
    createdAt:    string;
    state:        'open' | 'closed';
    replies:      number;
}

export interface PflowCommentReply {
    id:           number;
    author:       string;
    authorAvatar: string;
    text:         string;
    createdAt:    string;
}

export async function fetchComments(
    input: { owner: string; repo: string; target?: string; path?: string; state?: 'open' | 'all' },
): Promise<{ comments: PflowComment[] }> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/comments`, location.origin);
    if (input.target !== undefined) url.searchParams.set('target', input.target);
    if (input.path) url.searchParams.set('path', input.path);
    if (input.state) url.searchParams.set('state', input.state);
    return fetchJson<{ comments: PflowComment[] }>(url);
}

export async function fetchCommentThread(
    input: { owner: string; repo: string; id: number },
): Promise<{ replies: PflowCommentReply[] }> {
    return fetchJson<{ replies: PflowCommentReply[] }>(
        new URL(`/api/repo/${input.owner}/${input.repo}/comments/${input.id}/thread`, location.origin));
}

export async function replyToComment(
    input: { owner: string; repo: string; id: number; text: string },
): Promise<{ reply: PflowCommentReply }> {
    const res = await fetch(`/api/repo/${input.owner}/${input.repo}/comments/${input.id}/reply`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ text: input.text }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => null) as { message?: string } | null;
        throw new Error(err?.message ?? `POST reply ${res.status}`);
    }
    return res.json() as Promise<{ reply: PflowCommentReply }>;
}

export async function setCommentState(
    input: { owner: string; repo: string; id: number; state: 'open' | 'closed' },
): Promise<void> {
    const res = await fetch(`/api/repo/${input.owner}/${input.repo}/comments/${input.id}/state`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ state: input.state }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => null) as { message?: string } | null;
        throw new Error(err?.message ?? `POST state ${res.status}`);
    }
}

export async function createRepoIssue(
    input: { owner: string; repo: string; title: string; body?: string; labels?: string[]; path?: string },
): Promise<{ number: number; url: string }> {
    const res = await fetch(`/api/repo/${input.owner}/${input.repo}/issues`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ title: input.title, body: input.body, labels: input.labels, path: input.path }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => null) as { message?: string } | null;
        throw new Error(err?.message ?? `POST issues ${res.status}`);
    }
    return res.json() as Promise<{ number: number; url: string }>;
}

export interface RepoIssue {
    number:    number;
    title:     string;
    url:       string;
    labels:    string[];
    author:    string;
    createdAt: string;
}

export async function fetchIssues(
    input: { owner: string; repo: string; path?: string },
): Promise<{ issues: RepoIssue[] }> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/issues`, location.origin);
    if (input.path) url.searchParams.set('path', input.path);
    return fetchJson<{ issues: RepoIssue[] }>(url);
}

export interface ReportListItem { file: string; size: number }

export async function fetchReports(
    input: { owner: string; repo: string; ref?: string; path?: string },
): Promise<{ reports: ReportListItem[] }> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/reports`, location.origin);
    if (input.ref)  url.searchParams.set('ref', input.ref);
    if (input.path) url.searchParams.set('path', input.path);
    return fetchJson<{ reports: ReportListItem[] }>(url);
}

export async function fetchReportFile(
    input: { owner: string; repo: string; ref?: string; path?: string; file: string },
): Promise<{ file: string; content: string }> {
    const url = new URL(`/api/repo/${input.owner}/${input.repo}/reports`, location.origin);
    if (input.ref)  url.searchParams.set('ref', input.ref);
    if (input.path) url.searchParams.set('path', input.path);
    url.searchParams.set('file', input.file);
    return fetchJson<{ file: string; content: string }>(url);
}

export async function postComment(
    input: { owner: string; repo: string; target: string; text: string; path?: string },
): Promise<{ comment: PflowComment }> {
    const res = await fetch(`/api/repo/${input.owner}/${input.repo}/comments`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ target: input.target, text: input.text, path: input.path }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => null) as { message?: string } | null;
        throw new Error(err?.message ?? `POST comments ${res.status}`);
    }
    return res.json() as Promise<{ comment: PflowComment }>;
}

// ─── helpers ──────────────────────────────────────────────────────────

async function fetchJson<T>(url: URL): Promise<T> {
    const res = await fetch(url.pathname + url.search, { credentials: 'include' });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`${url.pathname} ${res.status}: ${text || res.statusText}`);
    }
    return await res.json() as T;
}
